import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail, ArrowLeft } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const ConfirmEmailChange: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { isAuthenticated, refreshUser } = useAuth();

  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'no-token'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      return;
    }

    const confirm = async () => {
      try {
        const response = await api.confirmEmailChange(token);
        setStatus('success');
        setMessage(response.message);
        if (isAuthenticated) {
          await refreshUser();
          setTimeout(() => navigate('/settings'), 3000);
        } else {
          setTimeout(() => navigate('/login'), 3000);
        }
      } catch (err) {
        setStatus('error');
        setMessage(err instanceof Error ? err.message : 'Confirmation failed');
      }
    };

    confirm();
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-8 text-center">
          {status === 'loading' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-500/10 mb-6">
                <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Confirming Email Change</h1>
              <p className="text-slate-400">Please wait...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/10 mb-6">
                <CheckCircle className="w-8 h-8 text-emerald-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Email Updated!</h1>
              <p className="text-slate-400 mb-4">{message}</p>
              <p className="text-sm text-slate-500">
                Redirecting to {isAuthenticated ? 'settings' : 'login'}...
              </p>
              <Link
                to={isAuthenticated ? '/settings' : '/login'}
                className="inline-flex items-center gap-2 mt-4 text-teal-400 hover:text-teal-300 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {isAuthenticated ? 'Go to Settings Now' : 'Go to Login Now'}
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 mb-6">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Confirmation Failed</h1>
              <p className="text-slate-400 mb-6">{message}</p>
              <p className="text-sm text-slate-500 mb-4">
                The link may have expired. Please request a new email change from Settings.
              </p>
              <Link
                to="/settings"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                Go to Settings
              </Link>
            </>
          )}

          {status === 'no-token' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 mb-6">
                <Mail className="w-8 h-8 text-amber-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">No Confirmation Token</h1>
              <p className="text-slate-400 mb-6">
                Please use the link from your email change confirmation email.
              </p>
              <Link
                to="/settings"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                Go to Settings
              </Link>
            </>
          )}
        </div>

        <p className="text-center text-slate-500 text-sm mt-6">
          Having trouble?{' '}
          <a href="mailto:support@marome-investments.co.za" className="text-teal-400 hover:text-teal-300">
            Contact Support
          </a>
        </p>
      </div>
    </div>
  );
};

export default ConfirmEmailChange;
